import { createSlug } from './urlHelpers';

/**
 * Transform raw listing to HomePageListingCard format
 * Used by RecentListings, PriceReductions, PremiumListings, FeaturedListings
 */
export const transformListingForCard = (listing) => {
  const publicData = listing.attributes?.publicData || {};
  const price = listing.attributes?.price;

  // Get image URLs from denormalized images
  const imageUrls = (listing.images || []).map(img => {
    const variants = img.attributes?.variants || {};
    return variants['listing-card-2x']?.url ||
           variants['listing-card']?.url ||
           variants['scaled-large']?.url ||
           variants['scaled-medium']?.url ||
           '';
  }).filter(Boolean);

  // Format price
  let formattedPrice = '';
  if (publicData?.isPOA) {
    formattedPrice = 'POA';
  } else if (price) {
    const { amount, currency } = price;
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100);
    formattedPrice = formattedAmount;
  }

  // Format mileage
  let formattedMileage = '';
  if (publicData?.Mileage && publicData?.miles_kms) {
    formattedMileage = `${Number(publicData.Mileage).toLocaleString()} ${publicData.miles_kms}`;
  }

  return {
    id: listing.id.uuid,
    title: listing.attributes?.title || '',
    images: imageUrls,
    year: publicData?.reg_year || '',
    transmission: publicData?.Transmission || '',
    fuelType: publicData?.Fuel || '',
    mileage: formattedMileage,
    drivingSide: publicData?.RHD_LHD || '',
    price: formattedPrice,
    status: publicData?.listing_level || '',
  };
};

/**
 * Adapt Sharetribe listing to NewListingCard format
 */
export const adaptListingToNewCard = (listing, intl) => {
  if (!listing || !listing.id) {
    return null;
  }

  const { id, attributes, images: listingImages } = listing;
  const { title, price, publicData = {}, geolocation } = attributes || {};

  // Extract images
  const images = listingImages && Array.isArray(listingImages)
    ? listingImages.map(img => img?.attributes?.variants?.['listing-card']?.url || img?.attributes?.variants?.default?.url).filter(Boolean)
    : [];

  // Note: Country data is now fetched asynchronously in the component
  // using getListingCountryFlag() for better accuracy

  // Format mileage with unit (same as original card)
  const formatMileageWithUnit = () => {
    if (!publicData?.Mileage || !publicData?.miles_kms) return null;
    const mileageValue = publicData.Mileage.toLocaleString();
    return `${mileageValue} ${publicData.miles_kms}`;
  };

  // Format price - check for POA first
  const formatPrice = () => {
    if (publicData?.isPOA) {
      return 'POA';
    }
    if (price) {
      const { amount, currency } = price;
      // Format without decimal places (no pence)
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount / 100);
      return formatted;
    }
    return 'POA';
  };

  return {
    id: id.uuid,
    slug: createSlug(title),
    images,
    title: title || 'Untitled',
    year: publicData?.reg_year || null,
    transmission: publicData?.Transmission || null,
    fuelType: publicData?.Fuel || null,
    mileage: formatMileageWithUnit(),
    price: formatPrice(),
    drivingSide: publicData?.RHD_LHD || null,
    status: publicData?.listing_level || null, // Pass the raw value for badge mapping
    engineCapacity: publicData?.engine_capacity || null,
    power: publicData?.power || null,
    bodyStyle: publicData?.body_style || null,
    exteriorColor: publicData?.exterior_color || null,
    interiorColor: publicData?.interior_color || null,
  };
};
